import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useLang } from "../context/LangContext";
import { useSettings } from "../context/SettingsContext";
import { api } from "../lib/api";
import { ArrowRight, Users } from "lucide-react";
import { getFeatureIcon } from "../lib/featureIcons";

export default function HomePage() {
  const { lang, t, pick } = useLang();
  const { settings, ready: settingsReady } = useSettings();
  const [houses, setHouses] = useState([]);
  const [features, setFeatures] = useState([]);

  useEffect(() => {
    api.get("/tiny-houses").then((r) => setHouses(r.data || []));
    api.get("/features").then((r) => setFeatures(r.data || []));
  }, []);

  // Only resolve the hero image once settings have loaded — this prevents the
  // bundled fallback from flashing before the admin-uploaded image appears.
  const heroImg = settingsReady
    ? (settings.hero_image_base64
        ? `data:image/jpeg;base64,${settings.hero_image_base64}`
        : settings.hero_image_url) ||
      "https://static.prod-images.emergentagent.com/jobs/4699da19-56e9-4739-b3c6-f66a2324e268/images/764419d9184b34c167ca983ba52c881cb3e7eef976bccf74717fb2ec539b993f.png"
    : null;

  return (
    <div>
      {/* Hero */}
      <section className="relative h-[92vh] min-h-[560px] w-full overflow-hidden" data-testid="hero">
        {heroImg ? (
          <img
            src={heroImg}
            alt="Minimodu tiny house in the forest"
            className="absolute inset-0 w-full h-full object-cover"
          />
        ) : (
          <div
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(135deg, var(--primary) 0%, color-mix(in srgb, var(--primary) 65%, black) 100%)",
            }}
            data-testid="hero-skeleton"
          />
        )}
        <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(0,0,0,0.6), rgba(0,0,0,0.15) 45%, rgba(0,0,0,0.25))" }} />
        <div className="relative z-10 h-full max-w-7xl mx-auto px-6 md:px-12 flex flex-col justify-end pb-24 md:pb-32 pt-28">
          <div className="max-w-3xl stagger">
            <span
              className="inline-block text-[11px] tracking-[0.3em] uppercase mb-5 px-3 py-1.5 rounded-full"
              style={{ background: "rgba(255,255,255,0.12)", backdropFilter: "blur(10px)", color: "white", animationDelay: "0.1s" }}
            >
              {lang === "mn" ? "Эко кэмпинг · Монгол" : "Eco Camping · Mongolia"}
            </span>
            <h1
              className="font-serif text-white leading-[1.05] text-5xl md:text-7xl lg:text-[88px] mb-6"
              style={{ animationDelay: "0.25s" }}
            >
              {lang === "mn" ? settings.site_title_mn : settings.site_title_en}
            </h1>
            <p
              className="text-white/90 text-lg md:text-xl max-w-xl leading-relaxed mb-10"
              style={{ animationDelay: "0.4s" }}
            >
              {lang === "mn" ? settings.tagline_mn : settings.tagline_en}
            </p>
            <div className="flex flex-wrap gap-3" style={{ animationDelay: "0.55s" }}>
              <Link to="/booking" className="btn btn-primary" data-testid="hero-book-btn">
                {t("book_now")} <ArrowRight size={16} />
              </Link>
              <Link
                to="/gallery"
                className="btn"
                style={{
                  background: "rgba(255,255,255,0.14)",
                  color: "white",
                  border: "1px solid rgba(255,255,255,0.25)",
                  backdropFilter: "blur(8px)",
                }}
                data-testid="hero-explore-btn"
              >
                {t("explore")}
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* About intro */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-36 grid md:grid-cols-12 gap-10 items-start" data-testid="about-section">
        <div className="md:col-span-5">
          <span className="text-xs tracking-[0.3em] uppercase opacity-60 block mb-4">
            {lang === "mn" ? "Минимоду гэж юу вэ" : "What is Minimodu"}
          </span>
          <h2 className="font-serif text-4xl md:text-6xl leading-[1.05]">
            {lang === "mn"
              ? "Бяцхан зайд, томоохон амралт"
              : "A small space for deep rest."}
          </h2>
        </div>
        <div className="md:col-span-6 md:col-start-7">
          <p className="text-lg md:text-xl leading-relaxed opacity-90 mb-6">
            {lang === "mn" ? settings.about_mn : settings.about_en}
          </p>
          <Link to="/gallery" className="inline-flex items-center gap-2 underline-offset-4 hover:underline opacity-80">
            {t("learn_more")} <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      {/* Features */}
      <section className="py-16 md:py-24 px-6 md:px-12" style={{ background: "color-mix(in srgb, var(--secondary) 45%, white)" }}>
        <div className="max-w-7xl mx-auto">
          <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
            <h2 className="font-serif text-4xl md:text-5xl max-w-xl">{t("features_title")}</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4" data-testid="features-list">
            {features.map((f) => {
              const Icon = getFeatureIcon(f.icon);
              return (
                <div key={f.id} className="card p-5 flex flex-col items-start gap-3" data-testid={`feature-card-${f.icon}`}>
                  <span
                    className="w-11 h-11 rounded-full flex items-center justify-center"
                    style={{ background: "var(--primary)", color: "var(--primary-fg)" }}
                  >
                    <Icon size={18} />
                  </span>
                  <div className="font-serif text-xl leading-tight">{pick(f, "name")}</div>
                  {f.price > 0 && (
                    <div className="text-xs opacity-60">
                      +{f.price.toLocaleString()} ₮
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Houses */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-24" data-testid="houses-section">
        <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
          <h2 className="font-serif text-4xl md:text-5xl">{t("houses_title")}</h2>
          <Link to="/booking" className="inline-flex items-center gap-2 underline-offset-4 hover:underline opacity-80">
            {t("book_now")} <ArrowRight size={16} />
          </Link>
        </div>
        <div className="grid md:grid-cols-2 gap-6">
          {houses.map((h) => (
            <div key={h.id} className="group overflow-hidden rounded-3xl card p-0" data-testid={`house-card-${h.id}`}>
              <div className="aspect-[4/3] overflow-hidden">
                {(h.image_base64 || h.cover_url) ? (
                  <img
                    src={h.image_base64 ? `data:image/jpeg;base64,${h.image_base64}` : h.cover_url}
                    alt={pick(h, "name")}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
                  />
                ) : (
                  <div className="w-full h-full" style={{ background: "var(--muted)" }} />
                )}
              </div>
              <div className="p-6 md:p-7">
                <div className="flex items-start justify-between gap-3">
                  <h3 className="font-serif text-3xl leading-tight">{pick(h, "name")}</h3>
                  <span className="inline-flex items-center gap-1.5 text-xs opacity-70">
                    <Users size={14} /> {h.capacity} {t("capacity")}
                  </span>
                </div>
                <p className="mt-3 opacity-80 leading-relaxed">{pick(h, "description")}</p>
                <div className="mt-5 flex items-baseline justify-between">
                  <div>
                    <div className="text-xs opacity-60">{t("from")}</div>
                    <div className="font-serif text-2xl">{h.price_per_night?.toLocaleString()} ₮ <span className="text-sm opacity-60">{t("per_night")}</span></div>
                  </div>
                  <Link to="/booking" className="btn btn-ghost btn-sm" data-testid={`house-book-${h.id}`}>
                    {t("book_now")}
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
